import { Chip, useTheme } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckListIcon from "@mui/icons-material/Checklist";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ScheduleIcon from "@mui/icons-material/Schedule";
import UpdateIcon from "@mui/icons-material/Update";

import { getRGBA } from "utils/color";
import { IStatusBadgeProps, PanelStatus } from "./StatusBadge.types";

/**
 * `PENDING` deixou de se chamar "Pendente" (2026-08-11, a pedido do Gustavo).
 * Com a chegada do `AGUARDANDO`, a tela passou a ter duas palavras
 * quase-sinônimas em português para coisas que exigem ações opostas:
 *
 *   - `PENDING`    — a execução **começou** e não terminou. Se envelhecer, é
 *                    uma execução travada (o spec §5 mediu 50 dessas).
 *   - `AGUARDANDO` — a execução **nem começou**, porque o passo anterior da
 *                    cadeia ainda não entregou. Não há nada travado.
 *
 * "Em andamento" × "Aguardando" separa as duas sem ambiguidade, e a ordem de
 * severidade não muda.
 */
export const STATUS_LABEL: Record<PanelStatus, string> = {
  SUCCESS: "Sucesso",
  FAILED: "Falhou",
  PENDING: "Em andamento",
  SEM_DADO: "Sem dado",
  PAROU: "Parou",
  NUNCA_RODOU: "Nunca rodou",
  AGUARDANDO: "Aguardando",
};

/**
 * `PAROU` não usa `warning.main` (#EF6C00). O `error.main` do tema é #BA1A1A:
 * os dois ficam a ~27° de matiz um do outro, na mesma família quente e com o
 * mesmo preenchimento a 8% — num chip pequeno, dentro de uma tabela densa,
 * leem-se como a mesma cor de alarme. Sob daltonismo protan/deutan (~8% dos
 * homens) eles colapsam praticamente no mesmo tom.
 *
 * Índigo resolve por matiz (~230°, o extremo oposto do vermelho) e sobrevive
 * aos dois tipos de daltonismo. Cinza estava descartado: é o que `SEM_DADO` e
 * `NUNCA_RODOU` já usam, e diria a coisa errada — "parou" é acionável,
 * "sem dado" não é.
 */
const PAROU_COLOR = "#303F9F";

/**
 * `AGUARDANDO` (T7) não é defeito: a atividade não rodou porque o passo
 * anterior da cadeia produziu massa nova, e ela sai do rollup de saúde
 * justamente por não julgar. A cor precisa dizer isso.
 *
 * **Âmbar foi descartado**, mesmo estando livre depois que o `PAROU` migrou
 * para índigo: é o erro que o T8 já documentou aqui — #EF6C00 fica a ~27° do
 * `error.main` (#BA1A1A), mesma família quente, e num chip pequeno os dois
 * leem como a mesma cor de alarme. Pintar de âmbar um estado que
 * explicitamente *não* acusa problema seria o pior dos dois mundos: alarme
 * falso e vocabulário errado.
 *
 * Teal (~174°) fica longe do vermelho (~0°) e do índigo do `PAROU` (~230°).
 * Risco residual assumido: sob protan/deutan ele se aproxima do verde de
 * `SUCCESS`. É a direção certa para errar — confundir "aguardando" com
 * "sucesso" mantém os dois no lado "não é problema", enquanto confundi-lo
 * com "falhou" inventaria um incidente. O ícone de ampulheta é o segundo
 * canal, independente de cor.
 */
const AGUARDANDO_COLOR = "#00796B";

/**
 * `PENDING` usava `theme.palette.error.main` — **exatamente o mesmo valor** de
 * `FAILED`, não uma cor parecida. Dois estados com ações diferentes eram
 * literalmente indistinguíveis. O plano já registrava isso como achado
 * lateral não corrigido; virou prioridade quando a tela ganhou o
 * `AGUARDANDO` e ficou impossível de ler.
 *
 * Âmbar é a escolha certa **aqui**, ao contrário do que foi para o `PAROU`
 * (ver acima): "em andamento" é de fato o irmão menor de "falhou", e o par
 * vermelho/âmbar é a convenção que todo operador já lê sem legenda. O que
 * tornava o âmbar errado no `PAROU` era semântico — pintar de alerta-menor um
 * pipeline morto —, não a cor em si.
 *
 * A proximidade de matiz com o vermelho continua existindo e é resolvida por
 * outros dois canais: o ícone de relógio (contra o X do `FAILED`) e, na lista
 * da carteira, o nome do estado escrito por extenso ao lado do ponto.
 */
const PENDING_COLOR = "#B26500";

/**
 * Mapa único de cor por estado. Exportado como hook para a lista da carteira
 * desenhar o ponto de status com a **mesma** fonte de verdade do chip — antes
 * a cor vivia só dentro deste componente, e qualquer outro lugar que quisesse
 * pintar um estado teria de repetir os valores à mão e sair de sincronia.
 */
export function useStatusColors(): Record<PanelStatus, string> {
  const theme = useTheme();

  return {
    SUCCESS: theme.palette.success.main,
    FAILED: theme.palette.error.main,
    PENDING: PENDING_COLOR,
    PAROU: PAROU_COLOR,
    NUNCA_RODOU: theme.palette.grey[600],
    SEM_DADO: theme.palette.grey[600],
    AGUARDANDO: AGUARDANDO_COLOR,
  };
}

export function StatusBadge({ status, label }: IStatusBadgeProps) {
  const theme = useTheme();
  const colorByStatus = useStatusColors();

  const iconByStatus: Record<PanelStatus, JSX.Element> = {
    SUCCESS: <DoneAllIcon />,
    FAILED: <CancelIcon />,
    PENDING: <ScheduleIcon />,
    PAROU: <UpdateIcon />,
    NUNCA_RODOU: <CheckListIcon />,
    SEM_DADO: <HelpOutlineIcon />,
    AGUARDANDO: <HourglassEmptyIcon />,
  };

  const color = colorByStatus[status];
  const isSemDado = status === "SEM_DADO";
  // Segundo canal, além da cor: `PAROU` ganha borda visível na própria cor.
  // O `SEM_DADO` já usava borda tracejada com o mesmo propósito. Distinguir
  // só por matiz deixaria a diferença invisível em impressão em preto e
  // branco, em telas de baixo contraste e para quem não distingue as cores.
  // `AGUARDANDO` (T7) usa a mesma borda sólida na própria cor, pelo mesmo
  // motivo: é um estado novo que precisa se distinguir sem depender só de
  // matiz.
  const hasOwnColorBorder = status === "PAROU" || status === "AGUARDANDO";

  function resolveBorderColor() {
    if (isSemDado) return theme.palette.grey[400];
    if (hasOwnColorBorder) return color;
    return "transparent";
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={iconByStatus[status]}
      label={label ?? STATUS_LABEL[status]}
      sx={{
        color,
        borderColor: resolveBorderColor(),
        borderStyle: isSemDado ? "dashed" : "solid",
        backgroundColor: isSemDado ? "transparent" : getRGBA(color, 0.08),
        fontWeight: 500,
        "& .MuiChip-icon": {
          color,
        },
      }}
    />
  );
}
